import { Component } from '@theme/component';

/**
 * @typedef {Object} SfGalleryNavRefs
 * @property {HTMLSpanElement} mobileLabel
 * @property {HTMLSelectElement} mobileSelect
 */

/** @extends {Component<SfGalleryNavRefs>} */
class SfGalleryNav extends Component {
  #controller = null;

  connectedCallback() {
    super.connectedCallback();
    // Auto-load the default tab when the gallery section has no products on page load
    requestAnimationFrame(() => this.#autoLoadIfEmpty());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  handleTabClick(event) {
    const tab = event.target.closest('[data-nav-tab]');
    if (!tab) return;
    const url = tab.dataset.url;
    if (!url || tab.getAttribute('aria-current') === 'true') return;
    this.#activate(tab, tab.textContent.trim());
    this.#load(url);
  }

  handleSelectChange(event) {
    const url = event.target.value;
    const selectedOption = event.target.selectedOptions[0];
    if (selectedOption?.dataset.link === 'true') {
      window.location.href = url;
      return;
    }
    const label = selectedOption?.text ?? '';
    const matchTab = this.querySelector(`[data-nav-tab][data-url="${url}"]`);
    if (matchTab) this.#activate(matchTab, label);
    this.#load(url);
  }

  #autoLoadIfEmpty() {
    const marker = document.querySelector('[data-gallery-main]');
    const gallerySection = marker?.closest('.shopify-section');
    if (!gallerySection) return;
    // results-list is only rendered when display_collection != blank
    if (gallerySection.querySelector('results-list')) return;
    const defaultTab = /** @type {HTMLElement|null} */ (
      this.querySelector('[data-nav-tab].gallery-nav__item--active[data-url]:not([data-url=""])') ??
      this.querySelector('[data-nav-tab][data-url]:not([data-url=""])')
    );
    if (!defaultTab) return;
    this.#activate(defaultTab, defaultTab.textContent.trim());
    this.#load(defaultTab.dataset.url ?? '');
  }

  async #load(url) {
    if (!url) return;
    const marker = document.querySelector('[data-gallery-main]');
    const section = marker?.closest('.shopify-section');
    if (!section) return;

    this.#controller?.abort();
    this.#controller = new AbortController();

    try {
      const res = await fetch(`${url}?sections=custom-gallery-main`, {
        signal: this.#controller.signal
      });
      if (!res.ok) return;
      const data = await res.json();
      const html = data['custom-gallery-main'];
      if (!html) return;

      section.outerHTML = html;

      // Force-load lazy images that browsers skip after outerHTML injection
      const newSection = document.querySelector('[data-gallery-main]')?.closest('.shopify-section');
      if (newSection) {
        const lazyImgs = /** @type {NodeListOf<HTMLImageElement>} */ (newSection.querySelectorAll('img[loading="lazy"]'));
        lazyImgs.forEach(img => { img.loading = 'eager'; });
      }
    } catch (e) {
      if (e.name !== 'AbortError') console.error('[GalleryNav]', e);
    }
  }

  #activate(activeTab, label) {
    this.querySelectorAll('[data-nav-tab]').forEach(tab => {
      const isActive = tab === activeTab;
      tab.classList.toggle('gallery-nav__item--active', isActive);
      if (isActive) {
        tab.setAttribute('aria-current', 'true');
      } else {
        tab.removeAttribute('aria-current');
      }
    });
    if (this.refs.mobileLabel) this.refs.mobileLabel.textContent = label;
    if (this.refs.mobileSelect) this.refs.mobileSelect.value = activeTab?.dataset.url ?? '';
  }
}

if (!customElements.get('sf-gallery-nav')) {
  customElements.define('sf-gallery-nav', SfGalleryNav);
}
